import React, { useEffect, useRef, useState } from 'react';
import { Box, Spinner } from '@amzn/awsui-components-core';
import { Dashboard } from '@amzn/cloudwatchdashboards-inside';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';

const CW_REGION = 'us-west-2';
const READY_TIMEOUT_MS = 15_000;

const getDashboardConfig = () => ({
  heightUnit: 40,
  displayMode: 'static' as const,
  widgetMarginX: 10,
  widgetMarginY: 10,
  style: 'polarisCard' as const,
  embeddedMaximize: true,
  internal: {
    accountId: ENVIRONMENT_VARIABLES.env.AwsAccountId,
    stage: 'prod'
  }
});

const IFRAME_STYLE = { width: '100%', height: '100%', border: 'none' };

/** Renders a single embedded CloudWatch dashboard. */
export const EmbeddedDashboard: React.FC<{ dashboardName: string }> = ({ dashboardName }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!containerRef.current) return;

    const cw = new Dashboard(containerRef.current, dashboardName, CW_REGION, getDashboardConfig(), IFRAME_STYLE);

    cw.on('ready', () => setLoading(false));
    cw.load(dashboardName);

    const timeout = setTimeout(() => setLoading(false), READY_TIMEOUT_MS);

    return () => {
      clearTimeout(timeout);
      cw.release();
    };
  }, [dashboardName]);

  return (
    <div style={{ position: 'relative', minHeight: 600, height: 'calc(100vh - 220px)' }} aria-label={`CloudWatch dashboard: ${dashboardName}`}>
      {loading && (
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
        </Box>
      )}
      <div ref={containerRef} style={{ height: '100%' }} />
    </div>
  );
};
